import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { GraduationCap, ArrowLeft, Eye, EyeOff, CheckCircle, Check, Video, BarChart, Zap } from 'lucide-react';

function SignupPage() {
  const [searchParams] = useSearchParams();
  const [packages, setPackages] = useState([]);
  const [formData, setFormData] = useState({
    institutionName: '',
    location: '',
    adminName: '',
    adminEmail: '',
    adminPassword: '',
    confirmPassword: '',
    selectedPackageId: null,
    learningMode: 'instructor_led' // Default to instructor-led
  });
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const navigate = useNavigate();

  useEffect(() => {
    fetchPackages();
  }, []);

  const fetchPackages = async () => {
    try {
      const response = await fetch('https://api.intuitlms.online/api/subscriptions/packages');
      const data = await response.json();
      setPackages(data);

      // Pre-select package from URL if available
      const packageParam = searchParams.get('package');
      if (packageParam && data.length > 0) {
        const pkg = data.find(p => p.name === packageParam);
        if (pkg) {
          setFormData(prev => ({ ...prev, selectedPackageId: pkg.id }));
        }
      } else if (data.length > 0) {
        // Default to first package (usually Basic)
        setFormData(prev => ({ ...prev, selectedPackageId: data[0].id }));
      }
    } catch (err) {
      console.error('Error fetching packages:', err);
    }
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-KE', {
      style: 'currency',
      currency: 'KES',
      minimumFractionDigits: 0
    }).format(amount);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    // Validation
    if (formData.adminPassword !== formData.confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    if (formData.adminPassword.length < 6) {
      setError('Password must be at least 6 characters long');
      return;
    }

    if (!formData.selectedPackageId) {
      setError('Please select a subscription package');
      return;
    }

    setIsLoading(true);

    try {
      const response = await fetch('https://api.intuitlms.online/api/auth/signup/college', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          institutionName: formData.institutionName,
          location: formData.location,
          adminName: formData.adminName,
          adminEmail: formData.adminEmail,
          adminPassword: formData.adminPassword,
          packageId: formData.selectedPackageId,
          learningMode: formData.learningMode
        }),
      });

      const data = await response.json();

      if (response.ok) {
        // Store credentials temporarily for success page
        localStorage.setItem('adminCredentials', JSON.stringify({
          email: data.adminCredentials.email,
          password: data.adminCredentials.password
        }));
        navigate('/success');
      } else {
        setError(data.message || 'Signup failed');
      }
    } catch (err) {
      setError('Network error. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-6">
            <Link to="/" className="flex items-center space-x-2 text-gray-600 hover:text-primary transition-colors">
              <ArrowLeft className="w-5 h-5" />
              <span>Back to Home</span>
            </Link>
            <div className="flex items-center space-x-2">
              <GraduationCap className="w-8 h-8 text-primary" />
              <span className="text-2xl font-bold text-primary">INTUIT LMS</span>
            </div>
          </div>
        </div>
      </header>

      {/* Signup Form */}
      <section className="py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-12">
            <h1 className="text-4xl font-bold text-gray-900 mb-4">
              Get Started with INTUIT LMS
            </h1>
            <p className="text-xl text-gray-600">
              Create your institution's account and start your 30-day free trial today.
            </p>
          </div>

          <div className="card">
            <form onSubmit={handleSubmit} className="space-y-8">
              {error && (
                <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
                  {error}
                </div>
              )}

              {/* Free Trial Banner */}
              <div className="bg-gradient-to-r from-green-500 to-emerald-600 text-white rounded-lg p-4 text-center">
                <p className="font-semibold text-lg">
                  🎉 30-Day Free Trial - Full Access to All Features!
                </p>
              </div>

              {/* Institution Details */}
              <div>
                <h2 className="text-xl font-bold text-gray-900 mb-4">Institution Information</h2>
                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <label htmlFor="institutionName" className="block text-sm font-medium text-gray-700 mb-2">
                      Institution Name *
                    </label>
                    <input
                      type="text"
                      id="institutionName"
                      name="institutionName"
                      required
                      value={formData.institutionName}
                      onChange={handleChange}
                      className="input-field"
                      placeholder="e.g., Nairobi Pentecostal Bible College"
                    />
                  </div>

                  <div>
                    <label htmlFor="location" className="block text-sm font-medium text-gray-700 mb-2">
                      Location *
                    </label>
                    <input
                      type="text"
                      id="location"
                      name="location"
                      required
                      value={formData.location}
                      onChange={handleChange}
                      className="input-field"
                      placeholder="e.g., Nairobi, Kenya"
                    />
                  </div>
                </div>
              </div>

              {/* Learning Model Selection */}
              <div>
                <h2 className="text-xl font-bold text-gray-900 mb-4">Learning Model</h2>
                <div className="grid md:grid-cols-2 gap-6">
                  {/* Instructor-Led Option */}
                  <div
                    onClick={() => setFormData({ ...formData, learningMode: 'instructor_led' })}
                    className={`border-2 rounded-xl p-6 cursor-pointer transition-all ${formData.learningMode === 'instructor_led'
                        ? 'border-blue-600 bg-blue-50'
                        : 'border-gray-200 hover:border-blue-300'
                      }`}
                  >
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="text-lg font-bold text-gray-900">Instructor-Led</h3>
                      {formData.learningMode === 'instructor_led' && (
                        <CheckCircle className="w-6 h-6 text-blue-600" />
                      )}
                    </div>
                    <p className="text-sm text-gray-600 mb-4">
                      Traditional term-based learning with live classes, scheduled assignments, and lecturer grading.
                    </p>
                    <ul className="space-y-2 text-sm text-gray-700">
                      <li className="flex items-start"><Check className="w-4 h-4 text-green-600 mr-2 mt-0.5" /> Live Virtual Classes</li>
                      <li className="flex items-start"><Check className="w-4 h-4 text-green-600 mr-2 mt-0.5" /> Term-Based Schedule</li>
                      <li className="flex items-start"><Check className="w-4 h-4 text-green-600 mr-2 mt-0.5" /> Lecturer Graded</li>
                    </ul>
                  </div>

                  {/* Self-Paced Option */}
                  <div
                    onClick={() => setFormData({ ...formData, learningMode: 'self_paced' })}
                    className={`border-2 rounded-xl p-6 cursor-pointer transition-all ${formData.learningMode === 'self_paced'
                        ? 'border-blue-600 bg-blue-50'
                        : 'border-gray-200 hover:border-blue-300'
                      }`}
                  >
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="text-lg font-bold text-gray-900">Self-Paced</h3>
                      {formData.learningMode === 'self_paced' && (
                        <CheckCircle className="w-6 h-6 text-blue-600" />
                      )}
                    </div>
                    <p className="text-sm text-gray-600 mb-4">
                      Flexible learning where students progress at their own speed with automated content release.
                    </p>
                    <ul className="space-y-2 text-sm text-gray-700">
                      <li className="flex items-start"><Check className="w-4 h-4 text-green-600 mr-2 mt-0.5" /> Flexible Schedule</li>
                      <li className="flex items-start"><Check className="w-4 h-4 text-green-600 mr-2 mt-0.5" /> Auto-Published Units</li>
                      <li className="flex items-start"><Check className="w-4 h-4 text-green-600 mr-2 mt-0.5" /> Mentor Support</li>
                    </ul>
                  </div>
                </div>
              </div>

              {/* Admin Account */}
              <div>
                <h2 className="text-xl font-bold text-gray-900 mb-4">Administrator Account</h2>
                <div className="space-y-6">
                  <div>
                    <label htmlFor="adminName" className="block text-sm font-medium text-gray-700 mb-2">
                      Administrator Name *
                    </label>
                    <input
                      type="text"
                      id="adminName"
                      name="adminName"
                      required
                      value={formData.adminName}
                      onChange={handleChange}
                      className="input-field"
                      placeholder="Full name of the administrator"
                    />
                  </div>

                  <div>
                    <label htmlFor="adminEmail" className="block text-sm font-medium text-gray-700 mb-2">
                      Administrator Email *
                    </label>
                    <input
                      type="email"
                      id="adminEmail"
                      name="adminEmail"
                      required
                      value={formData.adminEmail}
                      onChange={handleChange}
                      className="input-field"
                      placeholder="admin@yourinstitution.edu"
                    />
                  </div>

                  <div className="grid md:grid-cols-2 gap-6">
                    <div>
                      <label htmlFor="adminPassword" className="block text-sm font-medium text-gray-700 mb-2">
                        Password *
                      </label>
                      <div className="relative">
                        <input
                          type={showPassword ? 'text' : 'password'}
                          id="adminPassword"
                          name="adminPassword"
                          required
                          value={formData.adminPassword}
                          onChange={handleChange}
                          className="input-field pr-12"
                          placeholder="Minimum 6 characters"
                        />
                        <button
                          type="button"
                          onClick={() => setShowPassword(!showPassword)}
                          className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-gray-700"
                        >
                          {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                        </button>
                      </div>
                    </div>

                    <div>
                      <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700 mb-2">
                        Confirm Password *
                      </label>
                      <div className="relative">
                        <input
                          type={showConfirmPassword ? 'text' : 'password'}
                          id="confirmPassword"
                          name="confirmPassword"
                          required
                          value={formData.confirmPassword}
                          onChange={handleChange}
                          className="input-field pr-12"
                          placeholder="Confirm your password"
                        />
                        <button
                          type="button"
                          onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                          className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-gray-700"
                        >
                          {showConfirmPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Package Selection */}
              <div>
                <h2 className="text-xl font-bold text-gray-900 mb-4">Choose Your Package</h2>
                <div className="grid md:grid-cols-2 gap-6">
                  {packages.map((pkg) => (
                    <div
                      key={pkg.id}
                      onClick={() => setFormData(prev => ({ ...prev, selectedPackageId: pkg.id }))}
                      className={`border-2 rounded-xl p-6 cursor-pointer transition-all ${formData.selectedPackageId === pkg.id
                        ? 'border-blue-600 bg-blue-50'
                        : 'border-gray-200 hover:border-blue-300'
                        }`}
                    >
                      <div className="flex items-center justify-between mb-4">
                        <h3 className="text-xl font-bold text-gray-900">{pkg.displayName}</h3>
                        {formData.selectedPackageId === pkg.id && (
                          <CheckCircle className="w-6 h-6 text-blue-600" />
                        )}
                      </div>

                      <p className="text-3xl font-bold text-gray-900 mb-2">
                        {formatCurrency(pkg.pricePerStudentPerUnit)}
                      </p>
                      <p className="text-sm text-gray-600 mb-4">/student/unit</p>

                      <p className="text-gray-700 mb-4">{pkg.description}</p>

                      <ul className="space-y-2">
                        {pkg.features.slice(0, 4).map((feature, index) => (
                          <li key={index} className="flex items-start text-sm text-gray-700">
                            <Check className="w-4 h-4 text-green-600 mr-2 mt-0.5 flex-shrink-0" />
                            {feature}
                          </li>
                        ))}
                        {pkg.features.length > 4 && (
                          <li className="text-sm text-blue-600">
                            +{pkg.features.length - 4} more features
                          </li>
                        )}
                      </ul>
                    </div>
                  ))}
                </div>
              </div>

              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <div className="flex items-start">
                  <CheckCircle className="w-5 h-5 text-blue-600 mt-0.5 mr-3" />
                  <div className="text-sm text-blue-800">
                    <p className="font-medium mb-1">What happens next?</p>
                    <ul className="space-y-1 text-blue-700">
                      <li>• Your institution account will be created</li>
                      <li>• 30-day free trial will be activated automatically</li>
                      <li>• An admin account will be set up with the provided credentials</li>
                      <li>• You'll receive login details to access your admin dashboard</li>
                      <li>• Start adding courses, students, and lecturers immediately</li>
                    </ul>
                  </div>
                </div>
              </div>

              <button
                type="submit"
                disabled={isLoading}
                className="btn-primary w-full text-lg py-4 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isLoading ? 'Creating Account...' : 'Start Free Trial'}
              </button>
            </form>

            <div className="mt-6 text-center">
              <p className="text-gray-600">
                Already have an account?{' '}
                <a href="https://admin.intuitlms.online" className="text-primary hover:text-blue-700 font-medium">
                  Login to Admin Dashboard
                </a>
              </p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}

export default SignupPage;
